import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon
} from "@heroicons/react/16/solid";
import { Button } from "@moe/oss/ui/button";
import { cn } from "@moe/oss/utils/utils";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

interface Props {
  currentPage: number;
  totalPages?: number;
  onPageChange: (page: number) => void;
  maxVisiblePages?: number;
  className?: string;
}

export function Pagination({ currentPage, totalPages = 99, onPageChange, maxVisiblePages, className = "" }: Props) {
  const [pageInput, setPageInput] = useState<number>(currentPage);
  const [isEditing, setIsEditing] = useState(false);
  const [isFirstInput, setIsFirstInput] = useState(true);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setPageInput(currentPage);
    setIsFirstInput(true);
  }, [currentPage]);

  const handleInputChange = (e: any) => {
    let value = e.target.value;
    if (isFirstInput) {
      value = value[value.length - 1];
      setIsFirstInput(false);
    }
    setPageInput(value);
  };

  const handleInputBlur = () => {
    const newPage = Math.max(1, Math.min(pageInput || 1, totalPages));
    setIsEditing(false);
    setPageInput(currentPage);
    setIsFirstInput(true);

    if (newPage === currentPage) return;
    if (!pageInput) return;
    onPageChange(newPage);
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") handleInputBlur();
    if (e.key === "Escape") setIsEditing(false);
  };

  const { isDesktopScreen } = useAppContext();
  maxVisiblePages = maxVisiblePages ?? (isDesktopScreen ? 5 : 3);

  const getPageNumbers = () => {
    const halfVisible = Math.floor(maxVisiblePages / 2);
    let startPage = Math.max(currentPage - halfVisible, 1);
    const endPage = Math.min(startPage + maxVisiblePages - 1, totalPages);

    if (endPage - startPage + 1 < maxVisiblePages) startPage = Math.max(endPage - maxVisiblePages + 1, 1);

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const pageNumbers = getPageNumbers();

  const renderPageButton = (pageNum: number) => (
    <button
      key={pageNum}
      onClick={() => onPageChange(pageNum)}
      className={`p-2 ${
        pageNum === currentPage ? "text-tx-primary font-bold" : "text-tx-tertiary hover:text-tx-primary font-medium"
      }`}
    >
      {pageNum}
    </button>
  );
  return (
    <div className={cn("relative flex w-full flex-col items-center", className)}>
      <AnimatePresence>
        {isEditing && (
          <div className="absolute -top-12 flex w-full justify-center md:-top-8">
            <motion.input
              ref={(node) => {
                if (node) {
                  inputRef.current = node;
                  node.focus();
                }
              }}
              type="number"
              value={pageInput}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              className="border-line bg-nav-primary text-tx-primary  mb-2 w-14   rounded-md border py-2 text-center text-base transition duration-200 ease-out focus:outline-none focus:outline"
              aria-label="Current page"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.12 }}
            />
          </div>
        )}
      </AnimatePresence>
      <div
        className={cn(
          "bg-nav-primary  mx-auto  flex w-full items-center justify-center space-x-2.5 py-4 md:w-fit md:space-x-5 md:rounded-lg md:bg-transparent md:px-12",
          className
        )}
      >
        {isDesktopScreen && (
          <Button variant="shell" onClick={() => onPageChange(1)}>
            <ChevronDoubleLeftIcon className="size-4" />
          </Button>
        )}
        <Button variant="shell" onClick={() => onPageChange(currentPage - 1)}>
          <ChevronLeftIcon className="size-4" />
        </Button>

        {pageNumbers[0] > 1 && (
          <>
            {renderPageButton(1)}
            {pageNumbers[0] > 2 && (
              <button className="flex items-center md:p-2" onClick={() => setIsEditing(true)}>
                <EllipsisHorizontalIcon className="text-tx-tertiary size-5" />
              </button>
            )}
          </>
        )}

        {pageNumbers.map(renderPageButton)}

        {pageNumbers[pageNumbers.length - 1] < totalPages && (
          <>
            {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
              <button className="flex items-center md:p-2 " onClick={() => setIsEditing(true)}>
                <EllipsisHorizontalIcon className="text-tx-tertiary size-5" />
              </button>
            )}
            {renderPageButton(totalPages)}
          </>
        )}

        <Button variant="shell" onClick={() => onPageChange(currentPage + 1)}>
          <ChevronRightIcon className="size-4" />
        </Button>

        {isDesktopScreen && (
          <Button variant="shell" onClick={() => onPageChange(totalPages)}>
            <ChevronDoubleRightIcon className="size-4" />
          </Button>
        )}
      </div>{" "}
    </div>
  );
}
